import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { HiOutlineSave, HiOutlineExclamation } from 'react-icons/hi';
import './ReportForm.scss';

const ReportForm = ({ 
  currentReport, 
  onSubmit, 
  isLoading, 
  isReadOnly 
}) => {
  const [formData, setFormData] = useState({
    objectives: '',
    achievements: '',
    proudAchievements: '',
    measurableAchievements: '',
    improvements: '',
    feedback: ''
  });
  
  // Update form data when currentReport changes
  useEffect(() => {
    if (currentReport) {
      setFormData({
        objectives: currentReport.objectives || '',
        achievements: currentReport.achievements || '',
        proudAchievements: currentReport.proudAchievements || '',
        measurableAchievements: currentReport.measurableAchievements || '',
        improvements: currentReport.improvements || '',
        feedback: currentReport.feedback || ''
      });
    } else {
      // Reset form if no report
      setFormData({
        objectives: '',
        achievements: '',
        proudAchievements: '',
        measurableAchievements: '',
        improvements: '',
        feedback: ''
      });
    }
  }, [currentReport]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Handle Firebase timestamp object with seconds
    if (date && date.seconds) {
      return format(new Date(date.seconds * 1000), 'MMMM d, yyyy');
    }
    
    // Handle regular Date object
    return format(new Date(date), 'MMMM d, yyyy');
  };
  
  // Get score label based on 1-20 scale
  const getScoreLabel = (score) => {
    if (score >= 18) return 'Outstanding';
    if (score >= 15) return 'Exceeds Expectations';
    if (score >= 10) return 'Meets Expectations';
    if (score >= 5) return 'Needs Improvement';
    return 'Unsatisfactory';
  };
  
  // Format status for display
  const getStatusDisplay = () => {
    if (!currentReport) return '';
    
    if (currentReport.status === 'reviewed') {
      return (
        <div className="status reviewed">
          <span>Reviewed on {formatDate(currentReport.reviewedAt)}</span>
        </div>
      );
    } else if (currentReport.status === 'pending') {
      return (
        <div className="status pending">
          <span>Submitted on {formatDate(currentReport.submittedAt)}</span>
        </div>
      );
    } else if (currentReport.status === 'blank') {
      return (
        <div className="status blank">
          <span>New report - not yet submitted</span>
        </div>
      );
    }
    
    return '';
  };
  
  // Update form submit button text based on report status
  const getSubmitButtonText = () => {
    if (!currentReport) return 'Submit Report';
    
    if (currentReport.status === 'pending') {
      return 'Update Report';
    } else if (currentReport.status === 'blank') {
      return 'Submit Report';
    }
    
    return 'Submit Report';
  };
  
  return (
    <div className="report-form-container">
      {currentReport && (
        <div className="report-header">
          <div className="date-range">
            <span className="label">Week of:</span>
            <span className="value">
              {formatDate(currentReport.weekStartDate)} to {formatDate(currentReport.weekEndDate)}
            </span>
          </div>
          {getStatusDisplay()}
        </div>
      )}
      
      {!currentReport && (
        <div className="no-report-message">
          <HiOutlineExclamation className="icon" />
          <p>Select a week to submit a report.</p>
        </div>
      )}
      
      {currentReport && (
        <form className="report-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="objectives">Weekly Objectives</label>
            <textarea
              id="objectives"
              name="objectives"
              placeholder="What were your main objectives for this week?"
              value={formData.objectives}
              onChange={handleChange}
              disabled={isLoading || isReadOnly}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="achievements">Key Achievements</label>
            <textarea
              id="achievements"
              name="achievements"
              placeholder="What did you accomplish this week?"
              value={formData.achievements}
              onChange={handleChange}
              disabled={isLoading || isReadOnly}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="proudAchievements">Proud Achievements</label>
              <textarea
                id="proudAchievements"
                name="proudAchievements"
                placeholder="What are you most proud of accomplishing?"
                value={formData.proudAchievements}
                onChange={handleChange}
                disabled={isLoading || isReadOnly}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="measurableAchievements">Measurable Results</label>
              <textarea
                id="measurableAchievements"
                name="measurableAchievements"
                placeholder="What measurable results did you achieve?"
                value={formData.measurableAchievements}
                onChange={handleChange}
                disabled={isLoading || isReadOnly}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="improvements">Areas for Improvement</label>
            <textarea
              id="improvements"
              name="improvements"
              placeholder="What areas would you like to improve in next week?"
              value={formData.improvements}
              onChange={handleChange}
              disabled={isLoading || isReadOnly}
            />
          </div>
          
          {currentReport && currentReport.status === 'reviewed' && (
            <>
              <div className="form-group score-section">
                <label>Performance Score</label>
                <div className="score-display">
                  <div className="score-value">
                    {currentReport.score ? parseInt(currentReport.score, 10) : 'N/A'}
                    <span className="score-out-of">/20</span>
                  </div>
                  <div className="score-label">
                    {currentReport.score ? getScoreLabel(parseInt(currentReport.score, 10)) : 'Not scored'}
                  </div>
                </div>
              </div>
              
              <div className="form-group feedback-section">
                <label htmlFor="feedback">Manager Feedback</label>
                <div className="feedback-content">
                  {currentReport.feedback || 'No feedback provided yet.'}
                </div>
              </div>
            </>
          )}
          
          {!isReadOnly && (
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <HiOutlineSave className="icon" />
                    {getSubmitButtonText()}
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default ReportForm; 